import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { assetUrl } from "../../services/assetUrl";
import "./harshavardhini.css";

/* ---------- motion helpers ---------- */
const EASE = [0.22, 1, 0.36, 1];
const rise = {
  hidden: { opacity: 0, transform: "translateY(28px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.6, ease: EASE } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const Reveal = ({ children, className = "" }) => (
  <motion.div className={className} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-70px" }}>
    {children}
  </motion.div>
);

/* ---------- icons ---------- */
const s = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
const IChart = (<svg {...s}><path d="M3 3v18h18" /><path d="M7 15l3-3 3 3 5-6" /></svg>);
const IEye = (<svg {...s}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>);
const IStack = (<svg {...s}><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 16 9 5 9-5" /></svg>);
const IBook = (<svg {...s}><path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H20v15.5H5.5A1.5 1.5 0 0 0 4 20V4.5Z" /><path d="M4 20a1.5 1.5 0 0 1 1.5-1.5H20V21H5.5A1.5 1.5 0 0 1 4 19.5" /></svg>);
const IArrow = (<svg {...s} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const ICheck = (<svg {...s} strokeWidth="2.6"><path d="M20 6 9 17l-5-5" /></svg>);

/* ---------- content ---------- */
const PHOTO = "/images/faculty/harshavarshini.png";
const TOOLBOX = ["Power BI", "Microsoft Fabric", "Snowflake", "Tableau", "Python", "R", "SQL"];

const HERO_STATS = [
  { value: "3+", unit: "Years", label: "Teaching" },
  { value: "300+", unit: "Students", label: "Mentored" },
  { value: "6", unit: "Credentials", label: "Certified" },
];

const FOCUS = [
  { n: "01", title: "Data Analytics", icon: IChart, desc: "Data cleaning, transformation and statistical analysis that turns raw records into decisions.", tools: ["Python", "R", "SQL"] },
  { n: "02", title: "Data Visualization", icon: IEye, desc: "Insightful, decision-ready dashboards that make complex data legible at a glance.", tools: ["Power BI", "Tableau", "Fabric"] },
  { n: "03", title: "Data Engineering", icon: IStack, desc: "Building and managing resilient data pipelines and warehouses, end to end.", tools: ["Microsoft Fabric", "Snowflake"] },
  { n: "04", title: "Teaching", icon: IBook, desc: "Comprehensive lesson plans that map academic concepts onto real-world application.", tools: ["Curriculum", "Mentoring"] },
];

const DUTIES = [
  "Teach Data Analytics concepts to undergraduate and postgraduate students — data manipulation, statistical analysis and machine learning.",
  "Develop and deliver curriculum covering Power BI, Tableau, Microsoft Fabric and Snowflake, making complex topics accessible and engaging.",
  "Guide students through real-world data projects — teaching them to clean, analyse and visualise data for insight.",
  "Evaluate student progress and adapt teaching methods so every student grasps the key data concepts.",
  "Mentor students on capstone projects, helping them apply analytical tools to solve practical problems.",
  "Conduct workshops on advanced data visualisation and data-pipeline-management techniques.",
];

const CRED_GROUPS = [
  { issuer: "Microsoft", items: ["Power BI Data Analyst", "Microsoft Fabric Engineer Associate", "Power Platform Solution Architect", "Power Platform Fundamentals"] },
  { issuer: "Snowflake", items: ["Snowflake Platform Associate"] },
  { issuer: "Certiport", items: ["IT Specialist — Data Analytics"] },
];

const Harshavardhini = () => {
  return (
    <div className="hvx">
      <Header parentMenu="about" topbarEnable="enable" />

      {/* ============ HERO ============ */}
      <header className="hvx-hero">
        <span className="hvx-hero__grid" aria-hidden="true" />
        <span className="hvx-hero__glow" aria-hidden="true" />
        <div className="container">
          <div className="hvx-hero__inner">
            {/* text */}
            <div className="hvx-hero__text">
              <nav className="hvx-crumbs" aria-label="Breadcrumb">
                <Link to="/">Home</Link><span>/</span><span aria-current="page">Faculty Profile</span>
              </nav>
              <span className="hvx-eyebrow hvx-eyebrow--light">Faculty Profile</span>
              <h1 className="hvx-hero__name">J Harshavardhini</h1>
              <p className="hvx-hero__role">Lecturer <i>·</i> Data Science</p>
              <p className="hvx-hero__lede">
                Data Science faculty &amp; analyst — teaching the craft of turning
                data into decisions with Power BI, Microsoft Fabric, Snowflake and Tableau.
              </p>

              <div className="hvx-hero__stats">
                {HERO_STATS.map((st) => (
                  <div className="hvx-hstat" key={st.label}>
                    <span className="hvx-hstat__value">{st.value}<i>{st.unit}</i></span>
                    <span className="hvx-hstat__label">{st.label}</span>
                  </div>
                ))}
              </div>

              <div className="hvx-hero__tools">
                {TOOLBOX.map((t) => <span className="hvx-tag hvx-tag--light" key={t}>{t}</span>)}
              </div>
            </div>

            {/* portrait */}
            <div className="hvx-hero__media">
              <span className="hvx-hero__panel" aria-hidden="true" />
              <figure className="hvx-portrait">
                <img src={PHOTO} alt="Portrait of J Harshavardhini" />
              </figure>
              <span className="hvx-hero__badge">
                <b>Jul 2021 — Present</b>
                <em>NCET · Bengaluru</em>
              </span>
              <span className="hvx-hero__ribbon" aria-hidden="true">DATA SCIENCE</span>
            </div>
          </div>
        </div>
        <span className="hvx-hero__rule" aria-hidden="true" />
      </header>

      {/* ============ ABOUT ============ */}
      <section className="hvx-sec hvx-about">
        <div className="container">
          <div className="hvx-about__grid">
            <Reveal className="hvx-about__aside">
              <span className="hvx-eyebrow">About</span>
              <h2 className="hvx-h2">A data educator, by&nbsp;practice.</h2>
              <span className="hvx-about__rule" aria-hidden="true" />
              <p className="hvx-about__role">Lecturer · Data Science · NCET</p>
            </Reveal>
            <Reveal className="hvx-about__body">
              <p className="hvx-lead">
                Dedicated and experienced Lecturer in Data Science with over three years teaching
                Data Analytics at NCET — leveraging advanced tools such as Power BI, Microsoft
                Fabric, Snowflake and Tableau to deliver comprehensive education in data
                manipulation, visualisation, statistical analysis and data engineering.
              </p>
              <p>
                Certified across Microsoft, Certiport and Snowflake — pairing classroom teaching
                with current, applied industry credentials, and mentoring large cohorts of
                students toward professional certification.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ FOCUS — numbered index ============ */}
      <section className="hvx-sec hvx-sec--tint hvx-focus">
        <div className="container">
          <Reveal className="hvx-head">
            <span className="hvx-eyebrow">Focus Areas</span>
            <h2 className="hvx-h2">What I bring to the room.</h2>
            <p className="hvx-head__note">The competencies my teaching and projects are built on.</p>
          </Reveal>

          <motion.div className="hvx-index" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {FOCUS.map((f) => (
              <motion.article className="hvx-idx" key={f.n} variants={rise}>
                <span className="hvx-idx__n">{f.n}</span>
                <span className="hvx-idx__icon">{f.icon}</span>
                <div className="hvx-idx__main">
                  <h3 className="hvx-idx__title">{f.title}</h3>
                  <p className="hvx-idx__desc">{f.desc}</p>
                </div>
                <div className="hvx-idx__tools">
                  {f.tools.map((t) => <span className="hvx-tag" key={t}>{t}</span>)}
                </div>
                <span className="hvx-idx__arrow" aria-hidden="true">{IArrow}</span>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============ PHILOSOPHY — drenched navy ============ */}
      <section className="hvx-quote">
        <span className="hvx-quote__grid" aria-hidden="true" />
        <div className="container">
          <Reveal className="hvx-quote__inner">
            <span className="hvx-quote__mark" aria-hidden="true">&ldquo;</span>
            <p className="hvx-quote__text">
              Passionate about empowering students with practical, industry-relevant
              skills for <span>data-driven decision-making.</span>
            </p>
            <span className="hvx-quote__by">J Harshavardhini — Lecturer in Data Science</span>
          </Reveal>
        </div>
      </section>

      {/* ============ EXPERIENCE — light timeline ============ */}
      <section className="hvx-sec hvx-exp">
        <div className="container">
          <Reveal className="hvx-head">
            <span className="hvx-eyebrow">Experience</span>
            <h2 className="hvx-h2">In the classroom and beyond.</h2>
          </Reveal>

          <div className="hvx-exp__grid">
            <Reveal className="hvx-exp__meta">
              <span className="hvx-exp__now"><i className="hvx-dot" aria-hidden="true" />Currently</span>
              <h3 className="hvx-exp__role">Lecturer, Data Science</h3>
              <p className="hvx-exp__org">NCET · Bengaluru</p>
              <p className="hvx-exp__period">Jul 2021 — Present</p>
              <span className="hvx-tag hvx-tag--solid">Full-time faculty</span>
            </Reveal>

            <motion.ol className="hvx-spine" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
              {DUTIES.map((d, i) => (
                <motion.li className="hvx-spine__item" key={i} variants={rise}>
                  <span className="hvx-spine__node" aria-hidden="true" />
                  <p>{d}</p>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </div>
      </section>

      {/* ============ CREDENTIALS — grouped by issuer ============ */}
      <section className="hvx-sec hvx-sec--tint hvx-creds">
        <div className="container">
          <Reveal className="hvx-head">
            <span className="hvx-eyebrow">Credentials</span>
            <h2 className="hvx-h2">Verified, current certifications.</h2>
            <p className="hvx-head__note">Six active industry credentials across Microsoft, Snowflake and Certiport.</p>
          </Reveal>

          <motion.div className="hvx-creds__grid" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {CRED_GROUPS.map((g) => (
              <motion.div className="hvx-cred" key={g.issuer} variants={rise}>
                <div className="hvx-cred__head">
                  <span className="hvx-cred__issuer">{g.issuer}</span>
                  <span className="hvx-cred__count">{g.items.length}</span>
                </div>
                <ul className="hvx-cred__list">
                  {g.items.map((it) => (
                    <li className="hvx-cred__item" key={it}>
                      <span className="hvx-cred__check" aria-hidden="true">{ICheck}</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Harshavardhini;
